/**
 * PublicPageView — renders a Page Builder page fetched by slug.
 * Route: /:pageSlug  (own wrapper, no PublicLayout navbar)
 *
 * Header & Footer resolution (first match wins):
 *   1. page's own page_config.header / page_config.footer
 *   2. Home Page's page_config.header / page_config.footer (via usePublicIndex cache)
 *
 * Access rules:
 *  - Public pages: rendered immediately, no login required.
 *  - Private pages: if the API returns 401 (requires_auth), the user is
 *    redirected to /sign-in?next=<current path> so they can log in and
 *    be sent back here automatically.
 *
 * Embedded mode (`slug` + `embedded`): the feed shell renders this page inside
 * its middle column on phones and tablets (/feed/passes). There is no route
 * segment to read the slug from, and the shell already draws a header and a
 * footer of its own, so both of this page's own are suppressed — including
 * the member page's PublicHeader, which is otherwise unconditional. Neither
 * prop is passed by the /app/* route, so that path is untouched.
 */

import React, { useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { usePublicPage } from '../hooks/usePublicPage';
import { useLocalTemplate } from '../hooks/useLocalTemplate';
import { usePublicIndex } from '../hooks/usePublicIndex';
import { usePublicApp } from '../context/PublicAppContext';
import { useTheme } from '../theme/ThemeContext';
import DynamicHeader from '../components/DynamicHeader';
import PublicHeader from '../components/public/PublicHeader';
import { REGISTRATIONS_PATH } from './feed/feedRoutes';
import DynamicPageRenderer from '../components/dynamic-page/DynamicPageRenderer';
import { lastSectionOwnsScroll } from '../components/dynamic-page/sectionUtils';
import DynamicFooter, { resolveFooterMode } from '../components/dynamic-page/DynamicFooter';
import { buildFooterHtml } from '../utils/footerHtml';
import { applyPageSEO } from '../utils/seoHelper';
import PublicLoadingSkeleton from '../components/public/PublicLoadingSkeleton';
import PublicErrorPage from '../components/public/PublicErrorPage';
import PassCheckInBridge from '../components/athlohive/passes/PassCheckInBridge';

export default function PublicPageView({ slug, embedded = false }) {
  // ":pageSlug" for a single-segment slug, "*" for a path slug like
  // "my-home/test-world" (see the route pair in App.js). Reading both keeps
  // this component correct whichever of the two routes matched. An embedded
  // render matches no such route, so it names the slug outright.
  const params = useParams();
  const pageSlug = slug || params.pageSlug || params['*'] || '';
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const { setAppBranding } = usePublicApp();

  // A slug registered in localPages.js is served from committed files under
  // public/pages/<slug>/ and never touches the API. Anything else — including
  // a registered slug whose files turn out to be missing, unreadable or
  // non-public — resolves to null here and takes the API path unchanged.
  const { checking: checkingLocal, template } = useLocalTemplate(pageSlug);

  // Page-specific data. Skipped entirely once a local template has resolved,
  // which is the whole point: no request at all for those pages.
  const useApi = !checkingLocal && !template;
  const {
    data: apiData, isLoading: apiLoading, isError, error, refetch,
  } = usePublicPage(pageSlug, { enabled: useApi });

  const data = template || apiData;

  // Home page data — used as header/footer fallback when this page has no own config.
  // React Query deduplicates this request; if DynamicHomePage already fetched it the
  // result comes straight from cache at no extra network cost.
  const { data: indexData } = usePublicIndex();

  // Redirect to sign-in when the page is private and the user is not logged in.
  //
  // Bounce ONCE per path. The sign-in screen forwards a visitor it considers
  // signed in straight back to ?next=, so if its idea of "signed in" and this
  // page's idea of "may view" ever disagree — a session without membership of
  // this application, say — the two send each other in a circle forever, and
  // the server sees a hundred requests a second. One attempt, then show the
  // error instead: a person who genuinely needs to sign in still gets there,
  // and a mismatch surfaces as a message rather than a hang.
  const bounceKey = `arcee:signin-bounce:${location.pathname}`;
  useEffect(() => {
    if (!error?.requiresAuth) return;
    try {
      if (sessionStorage.getItem(bounceKey)) return;
      sessionStorage.setItem(bounceKey, '1');
    } catch (_) { /* private mode: fall through and bounce as before */ }
    const next = encodeURIComponent(location.pathname + location.search);
    navigate(`/sign-in?next=${next}`, { replace: true });
  }, [error, navigate, location, bounceKey]);

  // Signing in worked, so the next 401 on this path is a fresh problem.
  useEffect(() => {
    if (data) { try { sessionStorage.removeItem(bounceKey); } catch (_) { /* nothing to clear */ } }
  }, [data, bounceKey]);

  useEffect(() => {
    if (data?.seo) {
      const branding = data?.page_config?.app_branding || indexData?.app_branding;
      applyPageSEO(data.seo, branding);
    }
  }, [data, indexData]);

  // Propagate app branding so other context consumers stay in sync
  useEffect(() => {
    const branding = data?.page_config?.app_branding || indexData?.app_branding;
    if (branding) setAppBranding(branding);
  }, [data, indexData, setAppBranding]);

  // The member page ("My registrations") is one of the public header's own
  // tabs, so it keeps that header — My activities · Events · My registrations
  // — instead of whatever the Page Builder page draws; only its content
  // renders below. Which page that is comes from the app's member_link
  // branding, falling back to the slug PublicHeader links to by default.
  const memberSlug = indexData?.app_branding?.member_link?.page
    || REGISTRATIONS_PATH.replace(/^\/app\//, '');

  // Any other Page Builder page can show the same brand bar — logo and app
  // name, nothing else — by switching "Logo and app name" on in the
  // Header/Footer editor, which stores page_config.use_app_header. It borrows
  // PublicHeader so the lockup is identical to the rest of the site rather
  // than a DynamicHeader approximation of it. The flag only arrives with the
  // page's data, so the bar appears once the fetch resolves.
  //
  // "Include Header" off means no header at all, so it overrules the flag.
  const pageConfig = data?.page_config || {};

  // Opt-out flag — defaults to true so existing pages are unaffected. Pages
  // whose content already ships its own header (e.g. a full-page HTML/CSS
  // section) turn this off to suppress the platform's default header.
  // The footer has no equivalent flag: it is already gated by footer.enabled.
  const showHeader = pageConfig.show_header !== false;

  // The member page keeps the whole header — it is one of its own tabs — and
  // keeps it unconditionally, as it always has: "Include Header" has never
  // applied to it. A page that opts in gets the brand bar alone, and there
  // the flag is gated, so "Include Header" off still means no header at all.
  // Embedded: the feed shell is the chrome, so this page draws none.
  const memberPage = !embedded && pageSlug === memberSlug;
  const usePublicHeader = memberPage || (!embedded && showHeader && pageConfig.use_app_header === true);
  // Wraps whatever this page renders — loading, error or content — so the
  // header is there from the first paint rather than popping in afterwards.
  const withChrome = (node) => (usePublicHeader ? (
    <>
      <PublicHeader brandOnly={!memberPage} />
      {node}
    </>
  ) : node);

  // The same skeleton covers both paths, so a visitor cannot tell them apart.
  if (checkingLocal || (useApi && apiLoading)) return withChrome(<PublicLoadingSkeleton />);
  // Blank only while the sign-in redirect is on its way. If we have already
  // been there and come back still unauthorised, say so — a blank page tells
  // the person nothing and looks like a broken site.
  if (useApi && isError && error?.requiresAuth) {
    let bounced = false;
    try { bounced = !!sessionStorage.getItem(bounceKey); } catch (_) { /* treat as not bounced */ }
    return bounced ? withChrome(<PublicErrorPage error={error} onRetry={refetch} />) : null;
  }
  if (useApi && isError) return withChrome(<PublicErrorPage error={error} onRetry={refetch} />);
  if (!data) return null;

  const sections   = data.sections || [];
  const homeConfig = indexData?.custom_page?.page_config || {};
  const appBranding = indexData?.app_branding || {};

  // Prefer the page's own config; fall back to home page's config
  const headerCfg = pageConfig.header || homeConfig.header || null;
  const footerCfg = pageConfig.footer || homeConfig.footer || null;

  // A full-page HTML/CSS last section owns the scrollport, so rendering the
  // footer after it would give the visitor a second scrollbar. In that case the
  // footer is serialised into the iframe's document instead, keeping the page
  // on one continuous scroll.
  const embedFooter    = !embedded && !!footerCfg?.enabled && lastSectionOwnsScroll(sections);
  const embeddedFooter = embedFooter
    ? buildFooterHtml(footerCfg, theme, resolveFooterMode(footerCfg))
    : '';

  const content = (
    <>
      {showHeader && !embedded && !usePublicHeader && <DynamicHeader headerConfig={headerCfg} appBranding={appBranding} />}

      {sections.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <i className="bi bi-layout-text-window" style={{ fontSize: 40, opacity: 0.3 }} />
          <p className="mt-3">This page has no published content yet.</p>
        </div>
      ) : (
        <DynamicPageRenderer
          sections={sections}
          theme={theme}
          appBranding={appBranding}
          pageVisibility={data.visibility || 'public'}
          embeddedFooterHtml={embeddedFooter}
        />
      )}

      {!embedFooter && !embedded && <DynamicFooter footerCfg={footerCfg} theme={theme} />}

      {/* The pass cards' self check-in: the cards are a Page Builder frame,
          and the dialog and its rules live here. Also in the feed's embedded
          copy of this page, where `memberPage` is false. */}
      {pageSlug === memberSlug && <PassCheckInBridge />}
    </>
  );

  return withChrome(content);
}
